// WWW-15518 — Braze campaign catalog.
//
// Turns the flat Braze campaign list into the grouped/hierarchical segment tree
// the composer picker renders, plus the "combination" index that powers the
// pick-one-then-grey-out-the-rest gating and the single combined send.
//
// A campaign name encodes a segment path ("Topic > Sports > Eagles", "GEO >
// Philadelphia"). A *combined* campaign joins two atomic segments with " | "
// (e.g. "Topic > Food | GEO - Delaware"). The pipe is unambiguous (unlike "&",
// which appears inside atomic names like "TOPIC > Crime & Justice" / "Arts &
// Culture"), and as a safety net we still only treat a name as combined when
// each part already exists as its own atomic campaign.
//
// Names in Braze are inconsistent (TOPIC/Topic, GEO/Geo, ">" vs " - ", stray
// double spaces), so everything is matched on a normalized key.

import brazeService from './braze-service';

const SIZES_BATCH = 25; // Braze audience-estimate accepts at most 25 ids per call

// First breadcrumb → canonical group. Anything else (or no prefix) is "General".
const GROUP_LABELS = { topic: 'Topics', topics: 'Topics', geo: 'Geo' };
const GROUP_ORDER = ['General', 'Topics', 'Geo'];

// Split a name into breadcrumbs. ">" and " - " (spaces around the hyphen) both
// act as hierarchy separators; whitespace is collapsed.
const crumbsOf = (name) =>
  String(name || '')
    .replace(/\s*>\s*/g, '>')
    .replace(/\s+-\s+/g, '>')
    .split('>')
    .map((c) => c.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

// { group, path[], key, leaf } for a single (atomic) name.
export const normalizePath = (name) => {
  const crumbs = crumbsOf(name);

  if (crumbs.length === 0) return { group: 'General', path: [], key: '', leaf: '' };

  const first = crumbs[0].toLowerCase();
  const group = GROUP_LABELS[first] || 'General';
  const path = GROUP_LABELS[first] ? crumbs.slice(1) : crumbs;
  const key = [group, ...path].join(' > ').toLowerCase();

  return { group, path, key, leaf: path[path.length - 1] || crumbs[crumbs.length - 1] };
};

const extractSegmentId = (description) => {
  const m = /segment:([0-9a-f-]+)/i.exec(String(description || ''));

  return m ? m[1] : null;
};

// Build the catalog from the raw Braze campaign list (pure — no I/O).
export const buildCatalog = (rawCampaigns = []) => {
  const campaigns = rawCampaigns.filter((c) => c && c.id && c.name);

  // Every campaign's own normalized key (as if it were atomic). First one wins
  // so a part lookup resolves to a stable campaign.
  const keyToCampaign = new Map();

  campaigns.forEach((c) => {
    const { key } = normalizePath(c.name);

    if (key && !keyToCampaign.has(key)) keyToCampaign.set(key, c);
  });

  const atomicById = new Map();     // campaignId -> atomic node meta
  const keyToId = new Map();        // normalized key -> atomic campaignId
  const combos = [];                // [{ campaignId, name, memberIds: Set<atomicId> }]
  const nameById = new Map(campaigns.map((c) => [c.id, c.name])); // any campaign id -> name

  campaigns.forEach((c) => {
    const parts = String(c.name).split(/\s*\|\s*/);

    if (parts.length >= 2) {
      const memberCampaigns = parts.map((p) => keyToCampaign.get(normalizePath(p).key));
      const allResolve = memberCampaigns.every((m) => m && m.id !== c.id);

      if (allResolve) {
        combos.push({
          campaignId: c.id,
          name: c.name,
          memberIds: new Set(memberCampaigns.map((m) => m.id)),
        });

        return; // combined — not an atomic row
      }
    }

    const meta = normalizePath(c.name);

    atomicById.set(c.id, {
      campaignId: c.id,
      name: c.name,
      group: meta.group,
      path: meta.path.length ? meta.path : [meta.leaf],
      key: meta.key,
      segmentId: extractSegmentId(c.description),
    });
    if (meta.key && !keyToId.has(meta.key)) keyToId.set(meta.key, c.id);
  });

  return { atomicById, keyToId, combos, nameById };
};

// Grouped, hierarchical tree for the picker (General → Topics → Geo), derived
// from the atomic segments' breadcrumb paths. Returns:
//   [{ group, nodes: [{ id, label, campaignId?, segmentId?, selectable, children }] }]
export const getSegmentTree = (catalog) => {
  const groups = new Map();
  const ensureGroup = (name) => {
    if (!groups.has(name)) groups.set(name, { group: name, nodes: [], index: new Map() });

    return groups.get(name);
  };

  [...catalog.atomicById.values()].forEach((seg) => {
    const group = ensureGroup(seg.group);
    let siblings = group.nodes;
    let prefix = seg.group;

    seg.path.forEach((crumb, i) => {
      prefix += ` > ${crumb}`;
      let node = group.index.get(prefix);

      if (!node) {
        node = { id: prefix, label: crumb, children: [], selectable: false };
        group.index.set(prefix, node);
        siblings.push(node);
      }

      if (i === seg.path.length - 1) {
        node.selectable = true;
        node.campaignId = seg.campaignId;
        node.segmentId = seg.segmentId;
      }

      siblings = node.children;
    });
  });

  const ordered = GROUP_ORDER.filter((n) => groups.has(n))
    .concat([...groups.keys()].filter((n) => !GROUP_ORDER.includes(n)));

  // General is a flat list of standalone segments (no meaningful hierarchy), so
  // order it by label length shortest → longest (alphabetical tie-break) for a
  // tidy, scannable column. Topics / Geo keep their source (breadcrumb) order.
  const sortGeneral = (nodes) =>
    [...nodes].sort((a, b) => a.label.length - b.label.length || a.label.localeCompare(b.label));

  return ordered.map((name) => {
    const { group, nodes } = groups.get(name);

    return { group, nodes: group === 'General' ? sortGeneral(nodes) : nodes };
  });
};

// Resolve a selection of atomic campaign ids to the single campaign to send to:
//   1 id  → that atomic campaign
//   2+ ids → the combined campaign whose members are exactly that set
//   otherwise → null (caller blocks the send)
export const resolveTargetCampaignId = (catalog, selectedIds = []) => {
  const ids = [...new Set(selectedIds)];

  if (ids.length === 0) return null;
  if (ids.length === 1) return catalog.atomicById.has(ids[0]) ? ids[0] : null;

  const want = new Set(ids);
  const match = catalog.combos.find(
    (combo) => combo.memberIds.size === want.size && [...want].every((id) => combo.memberIds.has(id)),
  );

  return match ? match.campaignId : null;
};

// Given the current selection, which atomic campaign ids may still be picked?
// - nothing selected → every atomic id (each is sendable on its own)
// - otherwise → ids that keep the selection a subset of some combined campaign,
//   so the user can only ever build a combination that has a real campaign.
// (Already-selected ids are always allowed so they can be de-selected.)
export const getSelectableIds = (catalog, selectedIds = []) => {
  const allowed = new Set(selectedIds);

  if (selectedIds.length === 0) {
    catalog.atomicById.forEach((_, id) => allowed.add(id));

    return allowed;
  }

  const selected = new Set(selectedIds);

  catalog.combos.forEach((combo) => {
    const selectedInCombo = [...selected].every((id) => combo.memberIds.has(id));

    if (selectedInCombo) combo.memberIds.forEach((id) => allowed.add(id));
  });

  return allowed;
};

// Every atomic campaign id (for the picker's reach-count lookup).
export const getAtomicIds = (catalog) => [...catalog.atomicById.keys()];

// Always fetch the full Braze campaign list fresh — no caching.
export const loadCampaigns = async () => (await brazeService.listCampaigns()) || [];

// Per-campaign reachable-users estimate: { [campaignId]: size } for every
// campaign id (atomic + combined). Fetched fresh on every load — no caching.
export const loadSizes = async (campaignIds = []) => {
  const sizes = {};

  // The estimate endpoint accepts at most 25 ids per call, so batch + merge.
  const chunks = [];

  for (let i = 0; i < campaignIds.length; i += SIZES_BATCH) {
    chunks.push(campaignIds.slice(i, i + SIZES_BATCH));
  }

  try {
    const responses = await Promise.all(
      chunks.map((chunk) => brazeService.getAudienceEstimate(chunk).catch(() => ({ estimates: [] }))),
    );

    responses.forEach((res) => (res.estimates || []).forEach((e) => { sizes[e.campaign_id] = e.estimated_size; }));
  } catch (e) {
    console.error('Failed to load Braze campaign sizes', e);
  }

  return sizes;
};

export default {
  loadCampaigns,
  loadSizes,
  buildCatalog,
  getSegmentTree,
  resolveTargetCampaignId,
  getSelectableIds,
  getAtomicIds,
  normalizePath,
};
